import { Translation } from 'primeng/api';

/**
 * Tarih gösteriminin TEK kaynağı.
 *
 * Neden burada?
 * - PrimeNG 16'da `<p-calendar [locale]="...">` artık ÇALIŞMIYOR: setter yalnızca
 *   "Locale property has no effect, use new i18n API instead." uyarısı basıyor.
 *   Takvimin gün/ay adları PrimeNGConfig.setTranslation() ile verilir; bu yüzden
 *   çeviri tek yerden (AppComponent) uygulanır.
 * - Angular'ın `| date` pipe'ı LOCALE_ID'ye bakar. Kayıtlı bir locale olmadığı
 *   sürece en-US'e düşer ve "Aug 21, 2026" gibi İngilizce çıktı üretir.
 *   LOCALE_ID app.module.ts'te 'tr' olarak sağlanır.
 *
 * Yeni bir tarih alanı eklerken: HER ZAMAN `app-datepicker-input` kullan ve
 * biçim gerekiyorsa aşağıdaki sabitlerden birini geç. Şablona elle
 * `<input type="date">` veya `<p-calendar>` yazma.
 */

/** Angular LOCALE_ID ve tarih ayrıştırma için kullanılan dil kodu. */
export const UYGULAMA_LOCALE = 'tr';

/**
 * PrimeNG takviminin biçim sözdizimi Angular pipe'ından farklıdır
 * (yıl `yy` = 4 hane, ay `mm`, gün `dd`). İkisini karıştırma.
 */
export const TARIH_FORMAT = {
  /** p-calendar (app-datepicker-input) varsayılan biçimi → 21.08.2026 */
  takvim: 'dd.mm.yy',
  /** p-calendar saatli biçim; showTime ile birlikte kullanılır. */
  takvimSaatli: 'dd.mm.yy',
  /** Angular `| date` biçimi → 21.08.2026 */
  pipe: 'dd.MM.yyyy',
  /** Angular `| date` saatli biçim → 21.08.2026 14:30 */
  pipeSaatli: 'dd.MM.yyyy HH:mm',
  /** `<input type="date">` ve backend'e giden gün bazlı string biçimi. */
  isoGun: 'yyyy-MM-dd',
} as const;

/**
 * PrimeNG bileşenlerinin Türkçe metinleri.
 *
 * Yalnızca projede kullanılan bileşenlerin (takvim, tablo boş mesajı) anahtarları
 * doldurulmuştur; PrimeNG eksik anahtar için kendi İngilizce varsayılanını kullanır.
 */
export const PRIMENG_TR_CEVIRI: Translation = {
  dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
  dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
  dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
  monthNames: [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ],
  monthNamesShort: [
    'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz',
    'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara',
  ],
  dateFormat: TARIH_FORMAT.takvim,
  // Türkiye'de hafta pazartesi başlar (0 = Pazar).
  firstDayOfWeek: 1,
  today: 'Bugün',
  clear: 'Temizle',
  weekHeader: 'Hf',
  chooseDate: 'Tarih seçin',
  chooseMonth: 'Ay seçin',
  chooseYear: 'Yıl seçin',
  prevMonth: 'Önceki ay',
  nextMonth: 'Sonraki ay',
  prevYear: 'Önceki yıl',
  nextYear: 'Sonraki yıl',
  prevDecade: 'Önceki on yıl',
  nextDecade: 'Sonraki on yıl',
  prevHour: 'Önceki saat',
  nextHour: 'Sonraki saat',
  prevMinute: 'Önceki dakika',
  nextMinute: 'Sonraki dakika',
  prevSecond: 'Önceki saniye',
  nextSecond: 'Sonraki saniye',
  am: 'ÖÖ',
  pm: 'ÖS',
  accept: 'Evet',
  reject: 'Hayır',
  choose: 'Seç',
  upload: 'Yükle',
  cancel: 'Vazgeç',
  apply: 'Uygula',
  emptyMessage: 'Kayıt bulunamadı',
  emptyFilterMessage: 'Sonuç bulunamadı',
};

/**
 * Backend/`<input type="date">` için gün bazlı ISO string (yyyy-MM-dd) üretir.
 * `toISOString()` UTC'ye çevirdiği için yerel saatte gece yarısına yakın
 * değerlerde bir gün kaydırır; bu yüzden yerel alanlardan okunur.
 */
export function tarihiIsoGuneCevir(deger: Date | string | null | undefined): string {
  const tarih = tariheCevir(deger);
  if (!tarih) return '';
  const yil = tarih.getFullYear();
  const ay = `${tarih.getMonth() + 1}`.padStart(2, '0');
  const gun = `${tarih.getDate()}`.padStart(2, '0');
  return `${yil}-${ay}-${gun}`;
}

/** Gelen değeri (ISO string / Date / null) geçerli bir Date'e ya da null'a indirger. */
export function tariheCevir(deger: Date | string | null | undefined): Date | null {
  if (!deger) return null;
  const tarih = deger instanceof Date ? deger : new Date(deger);
  return isNaN(tarih.getTime()) ? null : tarih;
}
